import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, ApiClientError } from "../lib/api";
import ContentsStep from "./steps/ContentsStep";
import ParcelStep from "./steps/ParcelStep";
import SenderStep from "./steps/SenderStep";
import PreferencesStep from "./steps/PreferencesStep";
import RatesStep from "./steps/RatesStep";
import PaymentStep from "./steps/PaymentStep";
import AwaitingPaymentStep from "./steps/AwaitingPaymentStep";
import LabelStep from "./steps/LabelStep";
import type { RateQuote, RulesEvaluationResult, ShipmentIntentPublic, ShipmentPreferences } from "@shared/types";

type LocalStep = "loading" | "contents" | "parcel" | "sender" | "preferences" | "rates" | "payment" | "label" | "error";

const STEP_ORDER: LocalStep[] = ["contents", "parcel", "sender", "preferences", "rates", "payment", "label"];

export default function SendPage() {
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shipment, setShipment] = useState<ShipmentIntentPublic | null>(null);
  const [step, setStep] = useState<LocalStep>("loading");
  const [preferences, setPreferences] = useState<ShipmentPreferences | null>(null);
  const [quotes, setQuotes] = useState<RateQuote[]>([]);
  const [rulesEvaluation, setRulesEvaluation] = useState<RulesEvaluationResult | null>(null);
  const [unavailableProviders, setUnavailableProviders] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [selectingQuoteKey, setSelectingQuoteKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);

  const derivedStep = useCallback((s: ShipmentIntentPublic): LocalStep => {
    if (["FAILED", "LABEL_READY", "READY_FOR_DROPOFF", "COLLECTION_BOOKED", "IN_TRANSIT", "DELIVERED", "SHIPMENT_CREATING", "PAYMENT_CONFIRMED"].includes(s.status)) {
      return "label";
    }
    if (["CHECKOUT_CREATED", "AWAITING_PAYMENT", "PAYMENT_HANDOFF", "SERVICE_SELECTED"].includes(s.status)) return "payment";
    if (s.status === "RATES_READY") return "rates";
    if (s.status === "PARCEL_VALIDATED" || s.status === "CONTENTS_VALIDATED") {
      if (!s.sender) return "sender";
      return "preferences";
    }
    return "contents";
  }, []);

  const load = useCallback(async () => {
    if (!shipmentId) return;
    try {
      const s = await api.getShipmentIntent(shipmentId);
      setShipment(s);
      setPreferences(s.preferences);
      setStep(derivedStep(s));
    } catch {
      setStep("error");
    }
  }, [shipmentId, derivedStep]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle return from Stripe Checkout.
  useEffect(() => {
    if (!shipmentId) return;
    const paymentParam = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (paymentParam === "return" && sessionId) {
      setBusy(true);
      api
        .confirmPayment(shipmentId, sessionId)
        .then(({ shipment: updated }) => {
          setShipment(updated);
          setStep(derivedStep(updated));
        })
        .catch(() => setError("We couldn't confirm your payment automatically — checking again shortly."))
        .finally(() => {
          setBusy(false);
          searchParams.delete("payment");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
        });
    } else if (paymentParam === "cancelled") {
      setStep("payment");
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  // Poll while a shipment is being created server-side after payment, or
  // while it's in a FAILED-but-safe state — an admin retry
  // (functions/api/admin/shipment-intents/[id]/retry-fulfilment.ts) can
  // resolve this without the sender needing to refresh manually.
  useEffect(() => {
    if (!shipment || !shipmentId) return;
    if (!["SHIPMENT_CREATING", "PAYMENT_CONFIRMED", "FAILED"].includes(shipment.status)) return;
    const intervalMs = shipment.status === "FAILED" ? 15000 : 3000;
    const interval = setInterval(load, intervalMs);
    return () => clearInterval(interval);
  }, [shipment, shipmentId, load]);

  if (step === "loading") {
    return (
      <main id="main-content" className="page">
        <div className="spinner" role="status" aria-label="Loading" style={{ margin: "40vh auto" }} />
      </main>
    );
  }

  if (step === "error" || !shipment || !shipmentId) {
    return (
      <main id="main-content" className="page">
        <div className="card">
          <h1>We couldn't find this</h1>
          <p>This link may have expired. Please start again from the QR code or link you were given.</p>
        </div>
      </main>
    );
  }

  const currentIndex = STEP_ORDER.indexOf(step);
  // True once a provider-hosted checkout (currently only Parcel2Go) has
  // been created and we're waiting to hear back — the sender has either
  // just been sent off to pay, or has reloaded this page while still
  // unpaid. Distinct from the Stripe path, which never revisits "payment"
  // once CHECKOUT_CREATED — the return-URL/webhook pair resolves it first.
  const awaitingProviderPayment = shipment.checkout?.mode === "PROVIDER_HOSTED" && ["CHECKOUT_CREATED", "AWAITING_PAYMENT"].includes(shipment.status);

  return (
    <main id="main-content" className="page">
      {step !== "label" && (
        <div className="progress-track" aria-hidden="true">
          {STEP_ORDER.slice(0, 6).map((s, i) => (
            <div key={s} className={`progress-step ${i < currentIndex ? "done" : i === currentIndex ? "current" : ""}`} />
          ))}
        </div>
      )}

      {error && (
        <div className="banner banner-danger" role="alert" style={{ marginBottom: "var(--space-4)" }}>
          <span>{error}</span>
        </div>
      )}

      {step === "contents" && (
        <ContentsStep
          shipmentId={shipmentId}
          initial={shipment.contents}
          submitting={busy}
          onSubmit={async (contents) => {
            setBusy(true);
            setError(null);
            try {
              const updated = await api.updateContents(shipmentId, contents);
              setShipment(updated);
              setStep("parcel");
            } catch (err) {
              setError(err instanceof ApiClientError ? err.message : "Couldn't save — please try again.");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {step === "parcel" && (
        <ParcelStep
          initial={shipment.parcel}
          contents={shipment.contents}
          submitting={busy}
          onBack={() => setStep("contents")}
          onSubmit={async (parcel) => {
            setBusy(true);
            setError(null);
            try {
              const updated = await api.updateParcel(shipmentId, parcel);
              setShipment(updated);
              setStep(updated.sender ? "preferences" : "sender");
            } catch (err) {
              setError(err instanceof ApiClientError ? err.message : "Couldn't save — please try again.");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {step === "sender" && (
        <SenderStep
          initial={shipment.sender}
          submitting={busy}
          onBack={() => setStep("parcel")}
          onSubmit={async (sender) => {
            setBusy(true);
            setError(null);
            try {
              const updated = await api.updateSender(shipmentId, sender);
              setShipment(updated);
              setStep("preferences");
            } catch (err) {
              setError(err instanceof ApiClientError ? err.message : "Couldn't save — please try again.");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {step === "preferences" && (
        <PreferencesStep
          initial={preferences}
          submitting={busy}
          onBack={() => setStep("sender")}
          onSubmit={async (prefs) => {
            setPreferences(prefs);
            setBusy(true);
            setError(null);
            try {
              const res = await api.getRates(shipmentId, prefs);
              setShipment(res.shipment);
              setQuotes(res.quotes);
              setRulesEvaluation(res.rulesEvaluation);
              setUnavailableProviders(res.unavailableProviders);
              setStep("rates");
            } catch (err) {
              setError(err instanceof ApiClientError ? err.message : "Couldn't load shipping options — please try again.");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {step === "rates" && (
        <RatesStep
          quotes={quotes}
          rulesEvaluation={rulesEvaluation}
          unavailableProviders={unavailableProviders}
          preferences={preferences}
          contents={shipment.contents}
          selecting={selectingQuoteKey}
          onBack={() => setStep("preferences")}
          onSelect={async (quote: RateQuote) => {
            setSelectingQuoteKey(`${quote.providerId}:${quote.carrier}:${quote.service}`);
            setBusy(true);
            setError(null);
            try {
              const updated = await api.selectQuote(shipmentId, { providerId: quote.providerId, carrier: quote.carrier, service: quote.service });
              setShipment(updated);
              setRequiresConfirmation(false);
              setStep("payment");
            } catch (err) {
              setError(err instanceof ApiClientError ? err.message : "That option is no longer available — please pick another.");
            } finally {
              setSelectingQuoteKey(null);
              setBusy(false);
            }
          }}
        />
      )}

      {step === "payment" && awaitingProviderPayment && (
        <AwaitingPaymentStep
          shipment={shipment}
          onUpdated={(updated) => {
            setShipment(updated);
            setStep(derivedStep(updated));
          }}
        />
      )}

      {step === "payment" &&
        shipment.selectedQuote &&
        !awaitingProviderPayment && (
          <PaymentStep
            quote={shipment.selectedQuote}
            paying={busy}
            requiresConfirmation={requiresConfirmation}
            onBack={() => {
              setError(null);
              setStep("rates");
            }}
            onPay={async (confirmedHighValue) => {
              setBusy(true);
              setError(null);
              try {
                const { checkout } = await api.createCheckout(shipmentId, confirmedHighValue);
                if (checkout.mode === "PROVIDER_HOSTED") {
                  // Parcel2Go never redirects back (no configurable return
                  // URL — confirmed, see docs/parcel2go-payment-
                  // validation.md §H), so keep this tab on our own site and
                  // let the sender pay in a separate one; a reload picks up
                  // AwaitingPaymentStep above from the refreshed shipment
                  // status.
                  window.open(checkout.redirectUrl, "_blank", "noopener,noreferrer");
                  await load();
                } else {
                  window.location.href = checkout.redirectUrl;
                }
              } catch (err) {
                if (err instanceof ApiClientError && err.code === "REQUIRES_CONFIRMATION") {
                  setRequiresConfirmation(true);
                  setError(null);
                } else {
                  setError(err instanceof ApiClientError ? err.message : "Couldn't start payment — please try again.");
                }
              } finally {
                setBusy(false);
              }
            }}
          />
        )}

      {step === "label" && <LabelStep shipment={shipment} senderPostcode={shipment.sender?.postcode ?? null} />}
    </main>
  );
}
